import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useContentRefresh } from "@/lib/content";
import { toast } from "sonner";
import { Search, TrendingUp, Users, ShoppingBag, Truck, Loader2, PackageCheck, PackageOpen, Phone, MapPin, Copy, FileDown, Tag, Plus, Trash2, FileText, Save } from "lucide-react";

const STATUSES = ["preparing", "dispatched", "delivered"];
const PLANETS = ["moon", "mars"];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const refreshContent = useContentRefresh();
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [pricing, setPricing] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [content, setContent] = useState([]);
  const [frameOrders, setFrameOrders] = useState([]);
  const [frameQ, setFrameQ] = useState("");
  const [tab, setTab] = useState("orders");
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") {
      nav("/admin/login");
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const refresh = async () => {
    const [a, o, p, c, ct, fo] = await Promise.all([
      api.get("/admin/analytics"),
      api.get("/admin/orders", { params: q ? { q } : {} }),
      api.get("/pricing"),
      api.get("/admin/coupons"),
      api.get("/admin/content"),
      api.get("/admin/frame-orders"),
    ]);
    setAnalytics(a.data);
    setOrders(o.data);
    setPricing(p.data);
    setCoupons(c.data);
    setContent(ct.data);
    setFrameOrders(fo.data);
  };

  const searchFrameOrders = async (e) => {
    e.preventDefault();
    const { data } = await api.get("/admin/frame-orders", { params: frameQ ? { q: frameQ } : {} });
    setFrameOrders(data);
  };

  const updateFrameOrder = async (orderNumber, patch) => {
    try {
      const { data } = await api.put(`/admin/frame-orders/${orderNumber}/status`, patch);
      setFrameOrders((prev) => prev.map((o) => (o.order_number === orderNumber ? data : o)));
      toast.success("Frame order updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const search = async (e) => {
    e.preventDefault();
    const { data } = await api.get("/admin/orders", { params: q ? { q } : {} });
    setOrders(data);
  };

  const updateCourier = async (rn, patch) => {
    try {
      await api.put(`/admin/orders/${rn}/courier`, patch);
      toast.success("Order updated");
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const updatePrice = async (planet, size, price) => {
    try {
      await api.put(`/admin/pricing/${planet}/${size}`, { price_inr: Number(price) });
      toast.success("Price updated");
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const createCoupon = async (payload) => {
    try {
      await api.post("/admin/coupons", payload);
      toast.success("Coupon created");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const patchCoupon = async (id, patch) => {
    try {
      await api.put(`/admin/coupons/${id}`, patch);
      toast.success("Coupon updated");
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await api.delete(`/admin/coupons/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const saveContent = async (key, value, page, type, label) => {
    try {
      await api.put(`/admin/content/${key}`, { value, page, type, label });
      toast.success("Content saved");
      refreshContent();
      refresh();
    } catch {
      toast.error("Save failed");
    }
  };

  const downloadDeed = async (rn) => {
    try {
      const resp = await api.get(`/admin/orders/${rn}/deed.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Teuralink_Deed_${rn}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Deed downloaded");
    } catch {
      toast.error("Deed download failed");
    }
  };

  if (loading || !analytics) return <div className="pt-32 text-center text-white/60"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  return (
    <div className="pt-24 pb-16 px-6 lg:px-10 relative z-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-gold">Console</div>
            <h1 className="font-display text-4xl font-bold tracking-tight" data-testid="admin-title">Teuralink Admin</h1>
          </div>
          <div className="text-xs text-white/50 font-mono-space">
            Signed in as {user?.email}
          </div>
        </div>

        {/* Analytics */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <Stat Icon={ShoppingBag} label="Total Orders" value={analytics.total_orders} />
          <Stat Icon={TrendingUp} label="Total Revenue" value={`₹${analytics.total_revenue_inr.toLocaleString("en-IN")}`} />
          <Stat Icon={Users} label="Customers" value={analytics.total_customers} />
          <Stat Icon={PackageOpen} label="Preparing" value={analytics.by_courier_status?.preparing || 0} accent="amber" />
          <Stat Icon={Truck} label="Dispatched" value={analytics.by_courier_status?.dispatched || 0} accent="blue" />
          <Stat Icon={PackageCheck} label="Delivered" value={analytics.by_courier_status?.delivered || 0} accent="emerald" />
        </div>

        {/* Tabs */}
        <div className="mt-10 flex gap-2 border-b border-white/10 overflow-x-auto">
          {[["orders", "Orders"], ["frames", "Frame Orders"], ["pricing", "Pricing"], ["coupons", "Coupons"], ["content", "Content"], ["analytics", "Analytics"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`px-4 py-3 text-sm border-b-2 transition whitespace-nowrap ${
                tab === k ? "border-[#D4AF37] text-white" : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div className="mt-8">
            <form onSubmit={search} className="glass rounded-2xl p-2 flex items-center gap-2 mb-6">
              <Search className="w-4 h-4 ml-3 text-white/50" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by registration number, name, or email"
                data-testid="admin-search-input"
                className="flex-1 bg-transparent px-2 py-2 text-white placeholder:text-white/30 focus:outline-none"
              />
              <button className="btn-primary !py-2 !px-4 text-xs" data-testid="admin-search-btn">Search</button>
            </form>

            <div className="glass rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.03] border-b border-white/10">
                    <tr className="text-left overline">
                      <th className="p-4">Reg. Number</th>
                      <th className="p-4">Customer</th>
                      <th className="p-4">Planet</th>
                      <th className="p-4">Location</th>
                      <th className="p-4">Acres</th>
                      <th className="p-4">Amount</th>
                      <th className="p-4">Coupon</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Courier / Tracking</th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <AdminOrderRow key={o.id} order={o} onUpdate={updateCourier} onDeed={downloadDeed} />
                    ))}
                    {orders.length === 0 && (
                      <tr><td colSpan={10} className="p-8 text-center text-white/50">No orders found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "pricing" && (
          <div className="mt-8 space-y-8">
            {PLANETS.map((pl) => (
              <div key={pl}>
                <div className="overline text-gold mb-3 capitalize">{pl} Land Pricing</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {pricing.filter((p) => p.planet === pl).map((p) => (
                    <PriceCard key={`${pl}-${p.size}`} p={p} onSave={updatePrice} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "coupons" && (
          <CouponsTab
            coupons={coupons}
            onCreate={createCoupon}
            onPatch={patchCoupon}
            onDelete={deleteCoupon}
          />
        )}

        {tab === "content" && (
          <ContentTab items={content} onSave={saveContent} />
        )}

        {tab === "frames" && (
          <FrameOrdersTab
            orders={frameOrders}
            q={frameQ}
            setQ={setFrameQ}
            onSearch={searchFrameOrders}
            onUpdate={updateFrameOrder}
          />
        )}

        {tab === "analytics" && (
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="glass rounded-2xl p-6">
              <div className="overline">Orders by Planet</div>
              <div className="mt-4 space-y-3">
                {Object.entries(analytics.by_planet || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3">
                    <div className="w-24 capitalize text-white/70">{k}</div>
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#D4AF37]" style={{ width: `${(v / Math.max(1, analytics.total_orders)) * 100}%` }} />
                    </div>
                    <div className="font-mono-space text-white w-10 text-right">{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass rounded-2xl p-6">
              <div className="overline">Courier Status</div>
              <div className="mt-4 space-y-3">
                {Object.entries(analytics.by_courier_status || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3">
                    <div className="w-24 capitalize text-white/70">{k}</div>
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-white/60" style={{ width: `${(v / Math.max(1, analytics.total_orders)) * 100}%` }} />
                    </div>
                    <div className="font-mono-space text-white w-10 text-right">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ Icon, label, value, accent }) {
  const ring = {
    amber: "text-amber-300",
    blue: "text-blue-300",
    emerald: "text-emerald-300",
  }[accent] || "text-gold";
  return (
    <div className="glass rounded-2xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <Icon className={`w-4 h-4 ${ring}`} />
      </div>
      <div className="font-display text-3xl font-bold mt-2">{value}</div>
    </div>
  );
}

function PriceCard({ p, onSave }) {
  const [v, setV] = useState(p.price_inr);
  return (
    <div className="glass rounded-2xl p-6">
      <div className="overline">{p.acres} {p.acres === 1 ? "Acre" : "Acres"}</div>
      <div className="mt-3 flex items-center gap-2">
        <span className="text-gold">₹</span>
        <input
          type="number"
          value={v}
          onChange={(e) => setV(e.target.value)}
          data-testid={`price-input-${p.planet}-${p.size}`}
          className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-white font-mono-space"
        />
      </div>
      <button className="btn-ghost mt-4 w-full justify-center" onClick={() => onSave(p.planet, p.size, v)} data-testid={`price-save-${p.planet}-${p.size}`}>Save</button>
    </div>
  );
}

function AdminOrderRow({ order, onUpdate, onDeed }) {
  const [status, setStatus] = useState(order.courier_status);
  const [company, setCompany] = useState(order.courier_company || "");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr className="border-b border-white/5 hover:bg-white/[0.02]">
        <td className="p-4 font-mono-space text-gold text-xs">{order.registration_number}</td>
        <td className="p-4">
          <div className="text-white">{order.customer.full_name}</div>
          <div className="text-white/40 text-xs">admin@teuralink.com</div>
          <div className="text-white/60 text-xs font-mono-space mt-0.5">{order.customer.mobile}</div>
        </td>
        <td className="p-4 capitalize">{order.planet}</td>
        <td className="p-4">{order.marker_label}</td>
        <td className="p-4 font-mono-space">{order.acres}</td>
        <td className="p-4 font-mono-space">
          <div>₹{order.amount_inr}</div>
          {(order.discount_inr || 0) > 0 && (
            <div className="text-[10px] text-white/40">
              base ₹{order.base_amount_inr} · −₹{order.discount_inr}
            </div>
          )}
        </td>
        <td className="p-4 text-xs">
          {order.coupon_code ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 font-mono-space" data-testid={`row-coupon-${order.registration_number}`}>
              <Tag className="w-3 h-3" /> {order.coupon_code}
            </span>
          ) : (
            <span className="text-white/40">—</span>
          )}
        </td>
        <td className="p-4">
          <span className={`text-xs uppercase font-mono-space ${
            order.courier_status === "delivered" ? "text-emerald-300" :
            order.courier_status === "dispatched" ? "text-blue-300" : "text-amber-300"
          }`}>{order.courier_status}</span>
        </td>
        <td className="p-4 text-xs">
          <div>{order.courier_company || "—"}</div>
          <div className="text-white/40">{order.tracking_number || "—"}</div>
        </td>
        <td className="p-4">
          <div className="flex gap-2">
            <button
              className="btn-ghost !py-1 !px-3 text-xs"
              onClick={() => onDeed(order.registration_number)}
              data-testid={`deed-${order.registration_number}`}
              title="Download printable land deed PDF"
            >
              <FileDown className="w-3.5 h-3.5" /> Deed
            </button>
            <button className="btn-ghost !py-1 !px-3 text-xs" onClick={() => setExpanded(!expanded)} data-testid={`edit-${order.registration_number}`}>
              {expanded ? "Close" : "Edit"}
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-white/10 bg-white/[0.02]">
          <td colSpan={10} className="p-6">
            {/* Buyer shipping details — prominent */}
            <div className="glass rounded-2xl p-5 mb-5">
              <div className="flex items-center justify-between mb-4">
                <div className="overline text-gold">Buyer Shipping Details</div>
                <button
                  className="btn-ghost !py-1.5 !px-3 text-xs"
                  data-testid={`copy-address-${order.registration_number}`}
                  onClick={() => {
                    const block = `${order.customer.full_name}\n${order.customer.mobile}\n${order.customer.address}\n${order.customer.city}, ${order.customer.state} — ${order.customer.pin_code}\n${order.customer.country}\n${order.customer.email}`;
                    navigator.clipboard.writeText(block);
                    toast.success("Address block copied");
                  }}
                >
                  <Copy className="w-3.5 h-3.5" /> Copy full block
                </button>
              </div>
              <div className="grid sm:grid-cols-3 gap-5">
                <Detail label="Full Name" value={order.customer.full_name} />
                <Detail
                  label="Phone Number"
                  value={order.customer.mobile}
                  Icon={Phone}
                  testid={`buyer-phone-${order.registration_number}`}
                  onCopy={() => { navigator.clipboard.writeText(order.customer.mobile); toast.success("Phone copied"); }}
                />
                <Detail label="Email" value={order.customer.email} testid={`buyer-email-${order.registration_number}`} />
                <div className="sm:col-span-3">
                  <div className="overline mb-1">Complete Address</div>
                  <div className="text-white leading-relaxed" data-testid={`buyer-address-${order.registration_number}`}>
                    <MapPin className="w-4 h-4 inline text-gold mr-2 -mt-0.5" />
                    {order.customer.address}
                  </div>
                </div>
                <Detail label="City" value={order.customer.city} testid={`buyer-city-${order.registration_number}`} />
                <Detail label="State" value={order.customer.state} testid={`buyer-state-${order.registration_number}`} />
                <Detail label="PIN Code" value={order.customer.pin_code} mono testid={`buyer-pin-${order.registration_number}`} />
                <Detail label="Country" value={order.customer.country} />
              </div>
            </div>

            {/* Courier update controls */}
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-gold">Update Courier</div>
              <button
                onClick={() => onDeed(order.registration_number)}
                className="btn-primary !py-2 !px-4 text-xs"
                data-testid={`deed-expanded-${order.registration_number}`}
              >
                <FileDown className="w-3.5 h-3.5" /> Download Land Deed PDF
              </button>
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <label className="block">
                <span className="overline block mb-1">Status</span>
                <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`sel-status-${order.registration_number}`}
                  className="w-full bg-black border border-white/15 rounded-lg px-3 py-2 text-white">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline block mb-1">Courier Company</span>
                <input value={company} onChange={(e) => setCompany(e.target.value)} data-testid={`inp-company-${order.registration_number}`}
                  className="w-full bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-white" />
              </label>
              <label className="block">
                <span className="overline block mb-1">Tracking Number</span>
                <input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid={`inp-tracking-${order.registration_number}`}
                  className="w-full bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-white" />
              </label>
              <div className="flex items-end">
                <button
                  onClick={() => onUpdate(order.registration_number, { courier_status: status, courier_company: company, tracking_number: tracking })}
                  className="btn-primary w-full justify-center"
                  data-testid={`save-${order.registration_number}`}
                >Save</button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Detail({ label, value, Icon, mono, testid, onCopy }) {
  return (
    <div>
      <div className="overline mb-1 flex items-center gap-2">
        {Icon && <Icon className="w-3 h-3 text-gold" />}
        {label}
      </div>
      <div className={`text-white ${mono ? "font-mono-space" : ""} flex items-center gap-2`}>
        <span data-testid={testid}>{value}</span>
        {onCopy && (
          <button onClick={onCopy} className="opacity-40 hover:opacity-100 transition" title="Copy">
            <Copy className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// ------------------------- COUPONS TAB -------------------------
function CouponsTab({ coupons, onCreate, onPatch, onDelete }) {
  const [form, setForm] = useState({
    code: "",
    discount_type: "flat",
    discount_value: "",
    valid_from: "",
    valid_until: "",
    max_uses: "",
    enabled: true,
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.discount_value) {
      toast.error("Code and discount value required");
      return;
    }
    onCreate({
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
      valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      enabled: form.enabled,
    });
    setForm({ code: "", discount_type: "flat", discount_value: "", valid_from: "", valid_until: "", max_uses: "", enabled: true });
  };

  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="mt-8 space-y-6">
      <form onSubmit={submit} className="glass rounded-2xl p-6">
        <div className="overline text-gold mb-4 flex items-center gap-2">
          <Plus className="w-3 h-3" /> Create Coupon
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Cf label="Code">
            <input value={form.code} onChange={s("code")} data-testid="coupon-code" placeholder="LAUNCH20"
              className="w-full bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-white font-mono-space uppercase" />
          </Cf>
          <Cf label="Type">
            <select value={form.discount_type} onChange={s("discount_type")} data-testid="coupon-type"
              className="w-full bg-black border border-white/15 rounded-lg px-3 py-2 text-white">
              <option value="flat">Flat ₹</option>
              <option value="percent">Percent %</option>
            </select>
          </Cf>
          <Cf label={form.discount_type === "percent" ? "Percent" : "Amount (₹)"}>
            <input type="number" value={form.discount_value} onChange={s("discount_value")} data-testid="coupon-value"
              className="w-full bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-white font-mono-space" />
          </Cf>
          <Cf label="Max Uses (optional)">
            <input type="number" value={form.max_uses} onChange={s("max_uses")} data-testid="coupon-max-uses"
              className="w-full bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-white font-mono-space" />
          </Cf>
          <Cf label="Valid From (optional)">
            <input type="datetime-local" value={form.valid_from} onChange={s("valid_from")} data-testid="coupon-valid-from"
              className="w-full bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" />
          </Cf>
          <Cf label="Valid Until (optional)">
            <input type="datetime-local" value={form.valid_until} onChange={s("valid_until")} data-testid="coupon-valid-until"
              className="w-full bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" />
          </Cf>
          <div className="lg:col-span-2 flex items-end">
            <button className="btn-primary w-full justify-center" data-testid="coupon-create-btn">
              <Plus className="w-4 h-4" /> Create Coupon
            </button>
          </div>
        </div>
      </form>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/10">
              <tr className="text-left overline">
                <th className="p-4">Code</th>
                <th className="p-4">Discount</th>
                <th className="p-4">Validity</th>
                <th className="p-4">Uses</th>
                <th className="p-4">Enabled</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {coupons.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-white/50">No coupons yet.</td></tr>
              )}
              {coupons.map((c) => (
                <tr key={c.id} className="border-b border-white/5" data-testid={`coupon-row-${c.code}`}>
                  <td className="p-4 font-mono-space text-gold">{c.code}</td>
                  <td className="p-4 font-mono-space">
                    {c.discount_type === "percent" ? `${c.discount_value}%` : `₹${c.discount_value}`}
                  </td>
                  <td className="p-4 text-xs text-white/60">
                    <div>{c.valid_from ? new Date(c.valid_from).toLocaleDateString() : "—"} → {c.valid_until ? new Date(c.valid_until).toLocaleDateString() : "—"}</div>
                  </td>
                  <td className="p-4 font-mono-space text-white/80">
                    {c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => onPatch(c.id, { enabled: !c.enabled })}
                      data-testid={`coupon-toggle-${c.code}`}
                      className={`text-xs px-3 py-1 rounded-full font-mono-space uppercase ${
                        c.enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-white/50"
                      }`}
                    >
                      {c.enabled ? "Active" : "Disabled"}
                    </button>
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => onDelete(c.id)}
                      data-testid={`coupon-delete-${c.code}`}
                      className="text-white/40 hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Cf({ label, children }) {
  return (
    <label className="block">
      <span className="overline block mb-1">{label}</span>
      {children}
    </label>
  );
}

// ------------------------- CONTENT TAB -------------------------
function ContentTab({ items, onSave }) {
  const grouped = items.reduce((acc, it) => {
    (acc[it.page] = acc[it.page] || []).push(it);
    return acc;
  }, {});
  const pages = Object.keys(grouped).sort();
  return (
    <div className="mt-8 space-y-8">
      <div className="glass rounded-2xl p-5 flex items-start gap-3">
        <FileText className="w-5 h-5 text-gold shrink-0 mt-0.5" />
        <div className="text-sm text-white/70">
          Edit any piece of copy or content across the website. Changes are live immediately after Save — no code deploy required.
        </div>
      </div>
      {pages.map((page) => (
        <div key={page}>
          <div className="overline text-gold mb-3 uppercase">{page}</div>
          <div className="grid md:grid-cols-2 gap-4">
            {grouped[page].map((it) => (
              <ContentEditor key={it.key} item={it} onSave={onSave} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ContentEditor({ item, onSave }) {
  const [v, setV] = useState(item.value ?? "");
  const isLong = (item.type === "longtext") || (typeof item.value === "string" && item.value.length > 80);
  return (
    <div className="glass rounded-2xl p-5">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="overline">{item.label || item.key}</div>
          <div className="text-[10px] text-white/40 font-mono-space mt-0.5">{item.key}</div>
        </div>
      </div>
      {isLong ? (
        <textarea
          value={v}
          onChange={(e) => setV(e.target.value)}
          rows={4}
          data-testid={`content-input-${item.key}`}
          className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40"
        />
      ) : (
        <input
          value={v}
          onChange={(e) => setV(e.target.value)}
          data-testid={`content-input-${item.key}`}
          className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40"
        />
      )}
      <div className="mt-3 flex justify-end">
        <button
          className="btn-ghost !py-1.5 !px-3 text-xs"
          onClick={() => onSave(item.key, v, item.page, item.type, item.label)}
          data-testid={`content-save-${item.key}`}
        >
          <Save className="w-3.5 h-3.5" /> Save
        </button>
      </div>
    </div>
  );
}


const FRAME_STATUS_OPTIONS = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

function FrameOrdersTab({ orders, q, setQ, onSearch, onUpdate }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="mt-8" data-testid="frame-orders-tab">
      <form onSubmit={onSearch} className="glass rounded-2xl p-2 flex items-center gap-2 mb-6">
        <Search className="w-4 h-4 ml-3 text-white/50" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by order number, name, mobile, or planet"
          data-testid="frame-search-input"
          className="flex-1 bg-transparent px-2 py-2 text-white placeholder:text-white/30 focus:outline-none"
        />
        <button className="btn-primary !py-2 !px-4 text-xs" data-testid="frame-search-btn">Search</button>
      </form>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/10">
              <tr className="text-left overline">
                <th className="p-4">Order #</th>
                <th className="p-4">Frame</th>
                <th className="p-4 min-w-[280px]">Customer &amp; Shipping</th>
                <th className="p-4">Amount</th>
                <th className="p-4">Payment</th>
                <th className="p-4">Status</th>
                <th className="p-4">Placed</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <FrameOrderRow
                  key={o.id}
                  order={o}
                  expanded={expanded === o.order_number}
                  onExpand={() => setExpanded(expanded === o.order_number ? null : o.order_number)}
                  onUpdate={onUpdate}
                />
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-white/50">No frame orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FrameOrderRow({ order, expanded, onExpand, onUpdate }) {
  const [status, setStatus] = useState(order.order_status);
  const [courier, setCourier] = useState(order.courier_company || "");
  const [tracking, setTracking] = useState(order.tracking_number || "");

  const save = () => {
    onUpdate(order.order_number, {
      order_status: status,
      courier_company: courier,
      tracking_number: tracking,
    });
  };

  const statusColor = {
    pending: "text-amber-300",
    confirmed: "text-sky-300",
    shipped: "text-blue-300",
    delivered: "text-emerald-300",
    cancelled: "text-red-300",
  }[order.order_status] || "text-white";

  return (
    <>
      <tr className="border-b border-white/5 hover:bg-white/[0.02]">
        <td className="p-4 font-mono-space text-gold text-xs">{order.order_number}</td>
        <td className="p-4">
          <div className="flex items-center gap-3">
            <img src={order.frame_image_url} alt={order.frame_planet} className="w-12 h-16 object-cover rounded-md border border-white/10" />
            <div className="font-medium">{order.frame_planet}</div>
          </div>
        </td>
        <td className="p-4 min-w-[280px] max-w-[340px]">
          <div className="text-white font-medium">{order.customer.full_name}</div>
          <div className="text-white/60 text-xs font-mono-space mt-0.5" data-testid={`frame-mobile-${order.order_number}`}>
            📞 {order.customer.mobile}
          </div>
          <div className="text-white/50 text-xs mt-2 whitespace-pre-wrap leading-snug" data-testid={`frame-address-${order.order_number}`}>
            📍 {order.customer.address}
          </div>
        </td>
        <td className="p-4 font-mono-space">₹{order.amount_inr}</td>
        <td className="p-4">
          <span className="text-xs uppercase font-mono-space text-emerald-300">{order.payment_status}</span>
        </td>
        <td className="p-4">
          <span className={`text-xs uppercase font-mono-space ${statusColor}`}>{order.order_status}</span>
          {order.tracking_number && (
            <div className="text-[10px] text-white/40 mt-1 font-mono-space">{order.courier_company} · {order.tracking_number}</div>
          )}
        </td>
        <td className="p-4 text-xs text-white/50">{new Date(order.purchase_date).toLocaleDateString()}</td>
        <td className="p-4">
          <button className="btn-ghost !py-1 !px-3 text-xs" onClick={onExpand} data-testid={`frame-edit-${order.order_number}`}>
            {expanded ? "Close" : "Edit"}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-white/10 bg-white/[0.02]">
          <td colSpan={8} className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="overline text-gold mb-3">Shipping Address</div>
                <div className="glass rounded-2xl p-5 text-sm space-y-2">
                  <div><span className="text-white/50">Name: </span>{order.customer.full_name}</div>
                  <div><span className="text-white/50">Mobile: </span>{order.customer.mobile}</div>
                  <div className="whitespace-pre-wrap"><span className="text-white/50">Address: </span>{order.customer.address}</div>
                  <div className="text-xs text-white/40 pt-3 border-t border-white/10 mt-3">
                    Order ID: <span className="font-mono-space">{order.id}</span>
                  </div>
                </div>
              </div>
              <div>
                <div className="overline text-gold mb-3">Update Status</div>
                <div className="space-y-3">
                  <label className="block">
                    <span className="overline mb-2 block">Order Status</span>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      data-testid={`frame-status-${order.order_number}`}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-white capitalize focus:outline-none"
                    >
                      {FRAME_STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s} className="bg-black">{s}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="overline mb-2 block">Courier Company</span>
                    <input value={courier} onChange={(e) => setCourier(e.target.value)}
                      data-testid={`frame-courier-${order.order_number}`}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="overline mb-2 block">Tracking Number</span>
                    <input value={tracking} onChange={(e) => setTracking(e.target.value)}
                      data-testid={`frame-tracking-${order.order_number}`}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-white font-mono-space focus:outline-none" />
                  </label>
                  <button onClick={save} className="btn-primary !py-2 !px-4 text-xs" data-testid={`frame-save-${order.order_number}`}>
                    <Save className="w-3.5 h-3.5" /> Save
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
